import type {
	FilterRuleModel,
	MetricModel,
	OptimizationIDModel,
} from '@/shared/generated'

import { filter, pipe, prop, propEq, reduce } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	MetricGroup,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { formatNumber } from '@/shared/tools/locale'

export default function PromoWidget({
	handleNavigate,
	isLoading,
	queryParams,
}: {
	isLoading: boolean
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	handleNavigate: ({
		widgetFilter,
		widget,
	}: {
		widgetFilter: FilterRuleModel[]
		widget: string
	}) => void
}) {
	const { t } = useTranslation()

	const repricingStatisticQuery = useRepricingStatisticQuery(queryParams!, {
		enabled: !!queryParams && !isLoading,
		select: pipe(
			prop('metrics'),
			filter(propEq('group_type', MetricGroup.WithDiscount)),
			reduce(
				(acc, item: MetricModel) => {
					acc[item.group_type!] = item.value

					return acc
				},
				{} as Record<string, number>
			)
		),
	})
	if (repricingStatisticQuery.isLoading || isLoading) {
		return <LoadingPlaceholder />
	}
	if (repricingStatisticQuery.isError) {
		return (
			<Alert variant='destructive' className='w-full'>
				<AlertTriangleIcon />
				<AlertDescription>
					{repricingStatisticQuery.error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}
	const promoFilter = [
		{ name: 'new_discount_sum', operation: Operators.NE as const, value: 0 },
	]

	return (
		<div
			className='mb-1 flex cursor-pointer items-center justify-between text-sm font-normal hover:opacity-70'
			onClick={() =>
				handleNavigate({ widgetFilter: promoFilter, widget: 'promo widget' })
			}
		>
			<span>{t('status_panel_items_with_discount')}</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<span>
						{formatNumber(
							repricingStatisticQuery.data?.[MetricGroup.WithDiscount] || 0
						)}
					</span>
				</TooltipTrigger>
				<TooltipContent>PLs</TooltipContent>
			</Tooltip>
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='flex flex-col items-center space-y-2'>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
		</div>
	)
}
